export function createHomeMotion(runtime = window) {
  const document = runtime.document;
  const reducedMotion = runtime.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = runtime.navigator?.connection;
  const saveData = () => Boolean(connection?.saveData);
  const cleanups = new WeakMap();
  let installed = false;

  function bindHero(section) {
    const video = section.querySelector('[data-home-video]');
    const toggle = section.querySelector('[data-home-video-toggle]');
    if (!video) return () => {};
    let explicitlyPlaying = false;
    let manuallyPaused = false;
    let mediaFailed = false;
    let disposed = false;
    let playAttempt = 0;
    const canPlay = () => !disposed && !mediaFailed && (!reducedMotion.matches || explicitlyPlaying) && !document.hidden && (explicitlyPlaying || !saveData()) && !manuallyPaused;
    const showPoster = state => {
      section.dataset.videoState = state;
      if (toggle) {
        const paused = state !== 'playing';
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? 'Play background video' : 'Pause background video');
      }
    };
    const sync = () => {
      const currentAttempt = ++playAttempt;
      if (!canPlay()) {
        video.pause();
        showPoster(mediaFailed ? 'fallback' : 'paused');
        return;
      }
      if (section.dataset.videoState === 'playing') return;
      showPoster('loading');
      Promise.resolve(video.play()).then(() => {
        if (currentAttempt === playAttempt && canPlay()) showPoster('playing');
      }).catch(() => {
        if (currentAttempt === playAttempt && !disposed) {
          mediaFailed = true;
          video.pause();
          showPoster('fallback');
        }
      });
    };
    const onToggle = () => {
      if (section.dataset.videoState === 'playing' || section.dataset.videoState === 'loading') { manuallyPaused = true; explicitlyPlaying = false; }
      else { manuallyPaused = false; explicitlyPlaying = true; mediaFailed = false; }
      sync();
    };
    const onError = () => { playAttempt++; mediaFailed = true; video.pause(); showPoster('fallback'); };
    // The hero video never plays sound (owner, 24 Oct 2026): whatever unmutes it is undone.
    const onVolumeChange = () => { if (!video.muted) video.muted = true; };
    const onVisibilityChange = () => {
      if (document.hidden) { explicitlyPlaying = false; manuallyPaused = true; }
      sync();
    };
    const onPreferenceChange = () => { explicitlyPlaying = false; manuallyPaused = true; sync(); };
    toggle?.addEventListener('click', onToggle);
    video.addEventListener('error', onError);
    video.addEventListener('volumechange', onVolumeChange);
    document.addEventListener('visibilitychange', onVisibilityChange);
    reducedMotion.addEventListener('change', onPreferenceChange);
    connection?.addEventListener?.('change', sync);
    sync();
    return () => {
      disposed = true;
      playAttempt++;
      video.pause();
      toggle?.removeEventListener('click', onToggle);
      video.removeEventListener('error', onError);
      video.removeEventListener('volumechange', onVolumeChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      reducedMotion.removeEventListener('change', onPreferenceChange);
      connection?.removeEventListener?.('change', sync);
    };
  }

  function connect(section) {
    if (!section) return;
    cleanups.get(section)?.();
    cleanups.set(section, bindHero(section));
  }
  function disconnect(section) {
    if (!section) return;
    cleanups.get(section)?.();
    cleanups.delete(section);
  }
  function selectSection(event) {
    return event.target?.matches?.('[data-home-hero]')
      ? event.target
      : event.target?.querySelector?.('[data-home-hero]');
  }
  return {
    install() {
      if (installed) return;
      installed = true;
      document.querySelectorAll('[data-home-hero]').forEach(connect);
      document.addEventListener('shopify:section:load', event => connect(selectSection(event)));
      document.addEventListener('shopify:section:unload', event => disconnect(selectSection(event)));
    },
    connect,
    disconnect,
  };
}

if (typeof window !== 'undefined' && !window.__foldedLabelHomeMotion) {
  window.__foldedLabelHomeMotion = createHomeMotion(window);
  window.__foldedLabelHomeMotion.install();
}
